import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import AnimatedBackground from './components/common/AnimatedBackground';
import WelcomePage from './pages/WelcomePage';
import LoginPage from './pages/LoginPage';
import ReferralCodePage from './pages/ReferralCodePage';
import QuestionPage from './pages/QuestionPage';
import ReviewPage from './pages/ReviewPage';
import ThankYouPage from './pages/ThankYouPage';
import { FormProvider } from './context/FormContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useFormContext } from './context/FormContext';
import { useFormNavigation } from './hooks/useFormNavigation';
import { categories, totalSteps, findFirstBlankQuestionStep } from './utils/formHelpers';
import useAutoSave from './hooks/useAutoSave';
import { completeAssessment } from './firebase';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { currentUser, hasValidReferralCode } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  if (!hasValidReferralCode) {
    return <Navigate to="/referral" />;
  }
  
  return children;
};

// Layout wrapper to handle different page layouts
const LayoutWrapper = ({ children }) => {
  const location = useLocation();
  
  // Pages that should be vertically centered
  const centeredPages = ['/', '/login', '/referral', '/thanks'];
  const shouldCenter = centeredPages.includes(location.pathname);
  
  if (shouldCenter) {
    return (
      <div className="min-h-screen text-gray-800 flex flex-col items-center justify-center pt-4 pr-4 pb-4 pl-4">
        <AnimatedBackground />
        {children}
      </div>
    );
  }
  
  // For question pages: center horizontally but align to top vertically
  return (
    <div className="min-h-screen text-gray-800 flex flex-col items-center justify-start pt-4 pr-4 pb-4 pl-4">
      <AnimatedBackground />
      {children}
    </div>
  );
};

// The assessment content renderer
function AssessmentContent() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { 
    assessmentId, 
    setAssessmentId, 
    formData,
    isLoading, 
    hasLoadedFromFirestore,
    assessmentStatus  
  } = useFormContext();
  const initialAutoNavRef = useRef(false);
  
  const {
    currentStep,
    setCurrentStep,
    animateDirection,
    showReview,
    submitted,
    hasCompletedOnce,
    isEditingFromReview,
    progress,
    categoryInfo,
    nextStep,
    prevStep,
    backToReview,
    goToQuestion,
    handleSubmit,
    setShowReview,
    navigateToCategory
  } = useFormNavigation(totalSteps);
  
  // Check if assessment is already completed and redirect to thank you page
  useEffect(() => {
    if (hasLoadedFromFirestore && assessmentStatus === "completed") {
      console.log("Assessment already completed, redirecting to thank you page");
      navigate('/thanks');
    }
  }, [hasLoadedFromFirestore, assessmentStatus, navigate]);
  
  // Create or load assessment when component mounts
  useEffect(() => {
    const initializeAssessment = async () => {
      if (currentUser && !assessmentId) {
        try {
          // Try to load existing assessment from the ReferralCodePage
          const storedAssessmentId = localStorage.getItem('assessmentId');
          
          if (storedAssessmentId) {
            setAssessmentId(storedAssessmentId);
          }
        } catch (error) {
          console.error("Error initializing assessment:", error);
        }
      }
    };
    
    initializeAssessment();
  }, [currentUser, assessmentId, setAssessmentId]);
  
  // Jump to the first blank question (or last question if all filled)
  // ONLY after we've confirmed data is loaded from Firestore
  useEffect(() => {
    // Skip if data hasn't finished loading from Firestore
    if (!hasLoadedFromFirestore) return;
    
    // Skip if assessment is already completed
    if (assessmentStatus === "completed") return;
    
    // Only run auto-navigation once during initialization
    if (!isLoading && !initialAutoNavRef.current && !showReview) {
      const firstBlankStep = findFirstBlankQuestionStep(formData);
      
      console.log("Auto-navigating to first blank question:", firstBlankStep);
      
      // Jump immediately for a better user experience
      setCurrentStep(firstBlankStep);
      
      // Mark auto-navigation as done
      initialAutoNavRef.current = true;
      
      console.log("Initial auto-navigation done:", initialAutoNavRef.current);
    } else if (currentStep === 0 && !isLoading && !initialAutoNavRef.current) {
      // Default behavior: start at step 1 if no data is loaded yet
      setCurrentStep(1);
      initialAutoNavRef.current = true;
    }
  }, [formData, isLoading, currentStep, setCurrentStep, showReview, hasLoadedFromFirestore, assessmentStatus]);
  
  // Custom previous step handler
  const handlePrevStep = () => {
    if (currentStep === 1) {
      // If on first question, go back to welcome page
      navigate('/');
    } else {
      // Otherwise use normal prev step
      prevStep();
    }
  };
  
  // Handle submit with assessment completion
  const handleFormSubmit = async () => {
    if (assessmentId && currentUser) {
      try {
        // Call the original submit handler
        handleSubmit();
        
        // Mark assessment as completed in Firestore
        await completeAssessment(assessmentId, currentUser.uid);
      } catch (error) {
        console.error("Error completing assessment:", error);
      }
    } else {
      // Fallback to original handler if no assessment
      handleSubmit();
    }
  };
  
  // Redirect to thank you page if submitted
  useEffect(() => {
    if (submitted) {
      navigate('/thanks');
    }
  }, [submitted, navigate]);
  
  // Set up auto-save
  const { manualSave, hasUnsavedChanges } = useAutoSave(assessmentId);
  
  if (showReview) {
    return (
      <ReviewPage 
        onSubmit={handleFormSubmit}
        onBack={() => setShowReview(false)}
        onEditQuestion={goToQuestion}
        categories={categories}
      />
    );
  }

  return (
    <QuestionPage 
      categories={categories}
      currentStep={currentStep}
      animateDirection={animateDirection}
      categoryInfo={categoryInfo}
      progress={progress}
      nextStep={nextStep}
      prevStep={handlePrevStep} // Use custom handler instead
      backToReview={backToReview}
      hasCompletedOnce={hasCompletedOnce}
      isEditingFromReview={isEditingFromReview}
      totalSteps={totalSteps}
      navigateToCategory={navigateToCategory}
    />
  );
}

// Welcome page wrapper to handle navigation - preserves original UI
function WelcomeWrapper() {
  const navigate = useNavigate();
  const { assessmentStatus, hasLoadedFromFirestore } = useFormContext();
  
  const handleStartAssessment = () => {
    // If user has completed assessment, send them to thank you page
    if (assessmentStatus === "completed") {
      navigate('/thanks');
    } else {
      // Otherwise proceed with normal flow
      navigate('/login');
    }
  };
  
  return (
    <div className={`w-full max-w-3xl mx-auto transition-all duration-1000 opacity-100`}>
      {/* Standalone Brand Header Block - Taller with centered logo */}
      <div className="bg-blue-600 py-10 px-5 rounded-xl shadow-md mb-6 flex items-center justify-center">
        <img src="new-logo-white.png" alt="Careers Compass" className="h-16 object-contain" />
      </div>
      
      {/* Main Content Card - Reduced padding for vertical space */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-5 md:p-6">
        <WelcomePage next={handleStartAssessment} />
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <FormProvider>
        <Router>
          <LayoutWrapper>
            <Routes>
              <Route path="/" element={<WelcomeWrapper />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/referral" element={<ReferralCodePage />} />
              <Route path="/questions" element={
                <ProtectedRoute>
                  <AssessmentContent />
                </ProtectedRoute>
              } />
              <Route path="/thanks" element={<ThankYouPage />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </LayoutWrapper>
        </Router>
      </FormProvider>
    </AuthProvider>
  );
}

export default App;